import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EID } from '@/lib/element-ids.registry'
import { useStartListingAudit } from '@/hooks/useListingBuilder'
import { useProductPrefill } from '@/hooks/useAmazonAudit'
import { getProducts } from '@/lib/api/products'
import type { Product } from '@/types/products'
import { ProductSearchInput, SelectedProductBadge } from '@/components/v2/tools/amazon-listing/ProductSearchInput'
import { ChannelChips } from '@/components/v2/tools/amazon-listing/ChannelChips'

import type { RegisterSubmit, SourceSubmitStatus } from './sourceSubmit'

export function ListingChannelTab({
  onStarted,
  mode,
  registerSubmit,
  onStatusChange,
}: {
  onStarted: (jobId: string) => void
  mode?: 'small' | 'standard' | 'custom'
  registerSubmit?: RegisterSubmit
  onStatusChange?: (status: SourceSubmitStatus) => void
}) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<Product[]>([])
  const [showDrop, setShowDrop] = useState(false)
  const [product, setProduct] = useState<Product | null>(null)
  const [channelId, setChannelId] = useState<string | null>(null)
  const { data: prefill } = useProductPrefill(product?.id ?? null)
  const { mutate, isPending, error } = useStartListingAudit()

  useEffect(() => {
    const query = search.trim()
    const timer = setTimeout(async () => {
      if (!query) {
        setResults([])
        return
      }
      const resp = await getProducts({ page: 1, page_size: 20, search })
      setResults(resp.products ?? [])
    }, query ? 300 : 0)
    return () => clearTimeout(timer)
  }, [search])

  const selectProduct = (p: Product) => {
    setProduct(p); setChannelId(null); setSearch(''); setShowDrop(false)
  }

  const canSubmit = !!product && !!channelId && !isPending

  const handleSubmit = () => {
    if (!product || !channelId) return
    mutate(
      { source_ref: product.id, source_type: 'channel', channel_id: channelId, market_tld: 'de', mode },
      { onSuccess: (r) => onStarted(r.job_id) },
    )
  }

  useEffect(() => {
    registerSubmit?.(handleSubmit)
  })
  useEffect(() => {
    onStatusChange?.({
      canSubmit,
      isPending,
      submitId: EID['listing-builder-channel-submit-btn'],
      labelKey: 'listingBuilder.source.start',
    })
  }, [canSubmit, isPending, onStatusChange])

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
          {t('listingBuilder.source.channel.selectProduct')}
        </label>
        {product ? (
          <SelectedProductBadge product={product}
            onClear={() => { setProduct(null); setChannelId(null) }} />
        ) : (
          <ProductSearchInput search={search} setSearch={setSearch} onFocus={() => setShowDrop(true)}
            results={results} showDropdown={showDrop} onSelect={selectProduct}
            onBlur={() => setTimeout(() => setShowDrop(false), 200)} />
        )}
      </div>

      {product && prefill && prefill.channels.length > 0 && (
        <ChannelChips channels={prefill.channels} selected={channelId} onSelect={setChannelId} />
      )}
      {product && prefill && prefill.channels.length === 0 && (
        <p className="text-xs text-neutral-500">{t('listingBuilder.source.channel.noChannels')}</p>
      )}

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{t('listingBuilder.source.error')}</p>
      )}
    </div>
  )
}
